<script setup lang="ts">
import { type ButtonHTMLAttributes, computed } from 'vue'

import '@tix/ds/styles/portico/component/action-card.css'

export type ActionCardPadding = 'spacious' | 'comfy' | 'compact' | 'none'
export type ActionCardComponent = 'a' | 'button'

/** Roles that expose selection with `aria-checked`. `aria-pressed` is not allowed on these. */
const checkableRoles = new Set(['checkbox', 'menuitemcheckbox', 'menuitemradio', 'radio', 'switch'])

export type ActionCardProps = {
  /** Figma Padding axis */
  padding?: ActionCardPadding
  /**
   * Figma State=Active — persistent selected look, not CSS :active.
   * Buttons expose this as `aria-pressed`. Checkable roles expose it as `aria-checked`.
   */
  active?: boolean
  /** Figma State=Disabled */
  disabled?: boolean
  /** Root element. Use RouterLinkActionCard / ExternalLinkActionCard for navigation. */
  component?: ActionCardComponent
  /**
   * Accessible role override. `radio`, `checkbox`, `switch`, `menuitemradio`, and
   * `menuitemcheckbox` map `active` to `aria-checked` and omit `aria-pressed`.
   */
  role?: string
  /** Native button type. Bound only when `component` is `button`. */
  type?: ButtonHTMLAttributes['type']
}

const props = withDefaults(defineProps<ActionCardProps>(), {
  padding: 'spacious',
  component: 'button',
})

defineSlots<{
  default?: () => void
}>()

const isButton = computed(() => props.component === 'button')

const nativeType = computed(() => {
  if (!isButton.value) return props.type
  return props.type ?? 'button'
})

const nativeDisabled = computed(() => (isButton.value && props.disabled ? true : undefined))

const ariaDisabled = computed(() => {
  if (isButton.value || !props.disabled) return undefined
  return true
})

const tabIndex = computed(() => {
  if (isButton.value || !props.disabled) return undefined
  return -1
})

const isCheckable = computed(() => props.role !== undefined && checkableRoles.has(props.role))

const ariaChecked = computed(() => (isCheckable.value ? props.active === true : undefined))

const ariaPressed = computed(() => {
  if (!isButton.value || isCheckable.value) return undefined
  if (props.role !== undefined && props.role !== 'button') return undefined
  return props.active
})

const onRootClick = (event: MouseEvent) => {
  if (props.disabled && !isButton.value) {
    event.preventDefault()
    event.stopImmediatePropagation()
  }
}
</script>

<template>
  <component
    :is="component"
    class="action-card"
    :class="[`padding-${padding}`, { active, disabled: disabled && !isButton }]"
    :type="nativeType"
    :disabled="nativeDisabled"
    :role="role"
    :aria-disabled="ariaDisabled"
    :aria-checked="ariaChecked"
    :aria-pressed="ariaPressed"
    :tabindex="tabIndex"
    @click="onRootClick"
  >
    <span class="content">
      <slot />
    </span>
  </component>
</template>

<style scoped>
.action-card {
  display: block;
  width: 100%;
  margin: 0;
  appearance: none;
  font: inherit;
  color: inherit;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  overflow: clip;
  background: var(--action-card-bg);
  border: var(--border-default) solid var(--action-card-border);
  border-radius: var(--radius-medium);
  box-shadow: var(--action-card-shadow);
  transition:
    background-color var(--speed-medium) ease-in-out,
    border-color var(--speed-medium) ease-in-out,
    box-shadow var(--speed-medium) ease-in-out;

  &.padding-spacious {
    padding: var(--padding-spacious);
  }

  &.padding-comfy {
    padding: var(--padding-comfy);
  }

  &.padding-compact {
    padding: var(--padding-compact);
  }

  &.padding-none {
    padding: 0;
  }

  &:hover:not(:disabled):not(.disabled):not(.active) {
    background: var(--action-card-bg-hover);
    border-color: var(--action-card-border-hover);
  }

  &:focus-visible:not(:disabled):not(.disabled) {
    outline: none;
    background: var(--action-card-bg-hover);
    border-color: var(--action-card-border-hover);
    box-shadow:
      var(--action-card-shadow),
      0 0 0 4px var(--form-input-border-focus-visible),
      0 0 0 6px var(--form-input-border-focus-visible-outline);
  }

  &.active {
    background: var(--action-card-bg-active);
    border-color: var(--action-card-border-active);
  }

  &.active:focus-visible:not(:disabled):not(.disabled) {
    background: var(--action-card-bg-active);
    border-color: var(--action-card-border-active);
  }

  &:disabled,
  &.disabled {
    background: var(--action-card-bg-disabled);
    border-color: var(--action-card-border);
    box-shadow: none;
    cursor: not-allowed;
  }

  .content {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: var(--padding-comfy);
    width: 100%;
  }
}
</style>
